import { cast, flow, Instance, types } from "mobx-state-tree";
import { noop } from "rxjs";
import { markLastMessage } from "@fidget/common/closer";
import { logger } from "@fidget/common/utils";
import { conversationResourceDataToSnapshot } from "../base/Conversation";
import { determineInboxEntryFilters } from "../inbox/InboxFilter";
import { RoomStoreEventListener } from "./eventHandlers";
import { IRoom, Room } from "./Room";
import {
  servicesGetters,
  routerStoreGetter,
  calendarStoreGetter,
  currentUserProfileGetter,
  inboxStoreGetter,
  uiStoreGetter,
  currentUserStoreGetter
} from "../utils";
import { config } from "@fidget/common/config/config";

const RoomReferenceType = types.reference(
  types.late(() => Room),
  {
    get(id: string, parent: any) {
      // FIXME: `any` to avoid self-reference issue
      const { rooms } = parent;
      return rooms.get(id) as IRoom;
    },
    set(room: IRoom) {
      return (room && room.id) || "";
    }
  }
);

const roomStoreEventListenerName = "RoomStoreEventListener";

export const RoomStore = types
  .model("RoomStore", {
    rooms: types.map(types.late(() => Room)),
    // TODO: make references specifically target self.rooms
    currentRoom: types.maybe(RoomReferenceType)
  })
  .views(servicesGetters)
  .views(calendarStoreGetter)
  .views(routerStoreGetter)
  .views(currentUserProfileGetter)
  .views(currentUserStoreGetter)
  .views(inboxStoreGetter)
  .views(uiStoreGetter)
  .views((self) => ({
    getByOwnerId(id: string): IRoom | undefined {
      const room = [...self.rooms].find(([, r]) => r.owner.id === id);
      return (room && room[1]) || undefined;
    }
  }))
  .actions((self) => ({
    /**
     * Get the already loaded room and if exists execute callback, otherwise skip silently.
     * @param id the the identifier of requested room.
     * @param execute the callback.
     */
    find(id: string, execute: (room: IRoom) => void) {
      const room = self.rooms.get(id);
      if (room) {
        execute(room);
      }
    },

    setup: flow(function* () {
      if (self.rooms.size === 0) {
        return;
      }

      // get rid of all rooms apart from the current one
      self.rooms.replace([...self.rooms].filter(([, room]) => room === self.currentRoom));

      // refresh the current room.
      if (self.currentRoom) {
        try {
          const resource = yield self.spinnerService.getConversation(self.currentRoom.id);
          const updatedRoom = conversationResourceDataToSnapshot(resource);
          const previousAssignee = self.currentRoom.assignee?.id;
          self.currentRoom.update(updatedRoom);
          if (updatedRoom.assignee !== previousAssignee) {
            throw "Assignee has changed";
          }
          self.currentRoom.prepare();
        } catch (error) {
          logger.error({ message: "Couldn't setup room", error });
          self.currentRoom = undefined;
          self.routerStore.goToRoom(undefined, true);
        }
      }
    })
  }))
  .actions((self) => ({
    loadRoom: flow(function* (id: string) {
      try {
        const resource = yield self.spinnerService.getConversation(id);
        const initialData = conversationResourceDataToSnapshot(resource);

        // XXX: MST typing bug
        const room = Room.create(cast(initialData as any));
        return self.rooms.put(room);
      } catch (error) {
        logger.error({ message: "Couldn't load room", error });
        throw Error("Could not load conversation data");
      }
    }),
    maybeSnoozeOnLeave: (currentRoom?: IRoom) => {
      if (
        (config.services.snoozeOnLeave || self.currentUserStore.orgConfig?.snoozeOnRoomChange) &&
        currentRoom !== undefined &&
        currentRoom.isAssignedToMe &&
        !currentRoom.isSnoozed
      ) {
        currentRoom.toggleSnooze();
      }
    }
  }))
  .actions((self) => ({
    /**
     * Set current room - if it exists in the cache, point to it, otherwise fetch
     * data from backend and create a new instance.
     * @param {string} id  room ID
     */
    setCurrentRoom: flow(function* (id: string | undefined) {
      // unset room
      const currentRoom = self.currentRoom;

      if (id === undefined) {
        self.currentRoom = undefined;
        return;
      }

      let room = self.rooms.get(id);
      if (!room) {
        try {
          room = yield self.loadRoom(id);
          if (room) {
            room.prepare();
          }
        } catch (error) {
          logger.error({ message: "Couldn't set current room", error });
          // redirect and replace the wrong url with the default location.
          self.routerStore.goToRoom(undefined, true);
          return;
        }
      }

      if (room && room !== currentRoom) {
        if (self.calendarStore) {
          self.calendarStore.trash.hide();
        }

        self.artichokeService.join(room.id).catch(() => {
          noop();
        });
        self.currentRoom = room;

        // adjust inbox state to the selected room.
        const user = self.currentUserProfile;
        const filterIds = determineInboxEntryFilters(room, user && user.id, user && user.skills);
        if (filterIds && self.inboxStore !== undefined) {
          // FIXME: inbox store for some reason is undefined
          self.inboxStore.selectFilter(filterIds[0]!);
        }

        self.uiStore.removeChatNotification();

        self.maybeSnoozeOnLeave(currentRoom);
      }

      return self.currentRoom;
    })
  }))
  .actions((self) => ({
    afterCreate() {
      /**
       * Whenever dashboard becomes active sends mark about the last read message for the selected room.
       * Please note that messages are collected even if the room is in the background (eg. inactive tab).
       */
      self.userActivityService.onPageIsActive(() => {
        const { currentRoom } = self;
        if (currentRoom) {
          // FIXME: the call is using grouped entries - is this ok?
          markLastMessage(
            { artichokeService: self.artichokeService },
            currentRoom.id,
            currentRoom.chat.chatEntries
          );
        }
      });

      self.eventListener.addGlobalListener(
        new RoomStoreEventListener(() => self as IRoomStore, roomStoreEventListenerName)
      );
    },

    beforeDestroy() {
      self.rooms.forEach((room) => room.unsubscribeRoomListeners());
      self.eventListener.removeGlobalListener(roomStoreEventListenerName);
    }
  }))
  .actions((self) => ({
    /**
     * reset currently active room and hide chat view
     */
    resetCurrentRoom() {
      self.setCurrentRoom(undefined);
      self.routerStore.goToRoom(undefined, true);
    }
  }));

export type IRoomStore = Instance<typeof RoomStore>;
